<template>
  <div>
    <section-title class="mt-3" content="Datos del Participante" has-line />

    <div class="row my-3">
      <info-field
        class="col-12 col-md-4 my-2"
        label="Nombre del Participante:"
        :value="model.studentDetail?.mipeStudent?.fullName"
      />
      <info-field
        class="col-12 col-md-4 my-2"
        label="Número de SIE:"
        :value="model.student?.id"
      />
      <info-field
        class="col-12 col-md-4 my-2"
        label="Número de Registro:"
        :value="model.studentDetail?.mipeStudent?.regStudentId"
      />
    </div>

    <div class="row my-3">
      <info-field
        class="col-12 col-md-4 my-2"
        label="Edad Cronológica:"
        :value="model.studentDetail?.age"
      />
      <info-field
        class="col-12 col-md-4 my-2"
        format-type="date"
        label="Fecha de Nacimiento:"
        :value="model.studentDetail?.birthdate"
      />
      <info-field
        class="col-12 col-md-4 my-2"
        label="Impedimento:"
        :value="model.studentDetail?.mipeStudent?.impedimentDescription"
      />
    </div>

    <div class="participant-box mt-4">
      <div class="participant-box__title">
        Lenguaje Principal:
      </div>

      <div class="px-3 py-4">
        <div class="d-flex align-items-center flex-wrap primary-language-row">
          <label
            v-for="item in primaryLanguageOptions"
            :key="item.id"
            class="custom-control custom-radio custom-control-inline mb-2"
          >
            <input
              :checked="item.checked"
              class="custom-control-input"
              :disabled="isReadOnly"
              name="primaryLanguage"
              type="radio"
              @change="selectPrimaryLanguage(item.id)"
            />
            <span class="custom-control-label">{{ item.name }}</span>
          </label>

          <div v-if="isOtherLanguageSelected" class="other-language-input ml-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                custom-error-msg="El campo es requerido."
                :disabled="isReadOnly"
                :error="errors[0]"
                placeholder="Escriba el lenguaje"
                :value="selectedPrimaryLanguageOther"
                @input="updateOtherLanguage"
              />
            </validation-provider>
          </div>
        </div>
        <validation-provider
          v-slot="{ errors }"
          :rules="
            primaryLanguageOptions.some((x) => x.checked) ? '' : 'required'
          "
        >
          <error
            v-if="errors[0]"
            class="my-3"
            message="Debe seleccionar un lenguaje principal."
            variant="no-border"
          />
        </validation-provider>
      </div>
    </div>

    <div class="participant-box mt-3">
      <div class="participant-box__title">
        Modos de Comunicación o Expresión Oral:
      </div>

      <div class="row px-3 py-4">
        <div
          v-for="item in communicationOptions"
          :key="item?.id"
          class="col-12 col-md mb-2"
        >
          <check-box
            :class="isReadOnly ? 'disabled-check' : ''"
            :disabled="isReadOnly"
            :text="item.name"
            :value="Boolean(item.checked)"
            @input="toggleCommunication(item?.id)"
          />
        </div>
        <div class="col-12">
          <validation-provider
            v-slot="{ errors }"
            :rules="
              communicationOptions.some((x) => x.checked) ? '' : 'required'
            "
          >
            <error
              v-if="errors[0]"
              class="my-3"
              message="Debe seleccionar al menos un método de comunicación o expresión oral."
              variant="no-border"
            />
          </validation-provider>
        </div>
      </div>
    </div>
    <div class="participant-box mt-3">
      <div class="participant-box__title">
        Otras Condiciones del Estudiante:
      </div>

      <div class="row px-3 py-4">
        <div v-if="!isReadOnly" class="col-12 col-md-6 section-divider">
          <custom-drop-down
            id="otherConditions"
            v-model="model.otherCondition"
            :clearable="true"
            :disabled="isReadOnly"
            fieldkey="id"
            fieldname="name"
            label="Otras Condiciones"
            :options="availableOtherConditions"
            placeholder="Seleccione"
            @input="onOtherConditionChange"
          />
          <div v-if="isOtherConditionSelected" class="mt-2">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                custom-error-msg="El campo es requerido."
                :disabled="isReadOnly"
                :error="errors[0]"
                placeholder="Especifique la condición"
                :value="otherConditionCustomText"
                @input="otherConditionCustomText = $event"
              />
            </validation-provider>
          </div>
          <error
            v-if="otherConditionError"
            class="mt-2"
            :message="otherConditionError"
            variant="no-border"
          />
          <div class="d-flex justify-content-end mt-3">
            <action-button
              v-if="!isReadOnly"
              i="fas fa-file-plus"
              variant="success"
              @click="addOtherCondition"
            >
              Añadir
            </action-button>
          </div>
        </div>

        <div class="col-12 col-md-6">
          <div class="color-secondary-6 font-14 mb-3">
            Seleccionado(s):
          </div>
          <div class="d-flex flex-wrap">
            <div
              v-for="(item, index) in model.otherConditions"
              :key="index"
              class="condition-pill mr-2 mb-2"
            >
              <span>{{ item.other || item.name }}</span>

              <button
                v-if="!isReadOnly"
                class="condition-pill__remove"
                type="button"
                @click="removeOtherCondition(index)"
              >
                <i class="far fa-times-circle text-danger" />
              </button>
            </div>

            <div
              v-if="!model.otherConditions || !model.otherConditions.length"
              class="color-secondary-6 font-14"
            >
              <div>-</div>
              <validation-provider
                v-slot="{ errors }"
                :rules="model.otherConditions.length > 0 ? '' : 'required'"
              >
                <error
                  v-if="errors[0]"
                  message="Debe seleccionar al menos una condición del estudiante."
                  variant="no-border"
                />
              </validation-provider>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import InfoField from './InfoField.vue';
import vocationalEvaluationOptionTypes from '@/utils/constants/vocationalEvaluationOptionTypes';
import Error from '@/components/common/Error';

export default {
  name: 'ParticipantInfoSection',
  components: {
    SectionTitle,
    CustomDropDown,
    CustomInput,
    InfoField,
    Error,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    otherConditions: {
      type: Array,
      default: () => [],
    },
    communicationTypes: {
      type: Array,
      default: () => [],
    },
    mainLanguages: {
      type: Array,
      default: () => [],
    },
    validationErrors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      otherConditionError: null,
      otherConditionCustomText: '',
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    allOptions() {
      return this.model?.options || [];
    },

    primaryLanguageOptions() {
      return this.allOptions.filter(
        (x) => x?.type?.id === vocationalEvaluationOptionTypes.LenguajePrincipal
      );
    },

    selectedPrimaryLanguage() {
      return (
        this.primaryLanguageOptions.find((x) => Boolean(x?.checked)) || null
      );
    },

    selectedPrimaryLanguageOther() {
      return this.selectedPrimaryLanguage?.other || '';
    },

    communicationOptions() {
      return this.allOptions.filter(
        (x) =>
          x?.type?.id ===
          vocationalEvaluationOptionTypes.ModosComunicacionExpresionOral
      );
    },

    isOtherLanguageSelected() {
      return Boolean(this.selectedPrimaryLanguage?.isOther);
    },

    isOtherConditionSelected() {
      return this.model.otherCondition?.id === 256; //Otro
    },

    availableOtherConditions() {
      const selectedIds = (
        this.model.otherConditions.filter((x) => x.id !== 256) || []
      ).map((x) => x?.id);
      return (this.otherConditions || []).filter(
        (x) => !selectedIds.includes(x?.id)
      );
    },
  },

  created() {
    this.ensureModelDefaults();
  },

  methods: {
    ensureModelDefaults() {
      if (
        !Array.isArray(this.model.options) &&
        !Array.isArray(this.model.Options)
      ) {
        this.$set(this.model, 'options', []);
      }

      if (!Array.isArray(this.model.otherConditions)) {
        this.$set(this.model, 'otherConditions', []);
      }

      if (this.model.otherCondition === undefined) {
        this.$set(this.model, 'otherCondition', null);
      }
    },

    selectPrimaryLanguage(selectedId) {
      if (this.isReadOnly) return;

      const key = 'options';
      const updatedOptions = this.allOptions.map((item) => {
        if (
          item?.type?.id === vocationalEvaluationOptionTypes.LenguajePrincipal
        ) {
          const isSelected = item.id === selectedId;

          return {
            ...item,
            checked: isSelected,
            other: isSelected && item.isOther ? item.other || '' : null,
          };
        }

        return item;
      });

      this.$set(this.model, key, updatedOptions);
    },

    updateOtherLanguage(value) {
      if (this.isReadOnly) return;
      if (!this.isOtherLanguageSelected) return;

      const key = 'options';
      const selectedId = this.selectedPrimaryLanguage?.id;

      const updatedOptions = this.allOptions.map((item) => {
        if (item?.id === selectedId) {
          return {
            ...item,
            other: value,
          };
        }

        return item;
      });

      this.$set(this.model, key, updatedOptions);
    },

    toggleCommunication(optionId) {
      if (this.isReadOnly) return;

      const key = 'options';
      const updatedOptions = this.allOptions.map((item) => {
        if (
          item?.type?.id ===
            vocationalEvaluationOptionTypes.ModosComunicacionExpresionOral &&
          item?.id === optionId
        ) {
          return {
            ...item,
            checked: !item.checked,
          };
        }

        return item;
      });

      this.$set(this.model, key, updatedOptions);
    },

    onOtherConditionChange() {
      this.otherConditionError = null;
      if (!this.isOtherConditionSelected) {
        this.otherConditionCustomText = '';
      }
    },

    addOtherCondition() {
      if (this.isReadOnly) return;

      if (!this.model.otherCondition) {
        this.otherConditionError =
          'Debe seleccionar una condición antes de añadir.';
        return;
      }

      if (
        this.isOtherConditionSelected &&
        !this.otherConditionCustomText.trim()
      ) {
        this.otherConditionError = 'Debe especificar la condición.';
        return;
      }

      const list = [...(this.model.otherConditions || [])];

      if (this.isOtherConditionSelected) {
        const trimmed = this.otherConditionCustomText.trim();
        const duplicateText = list.some(
          (x) => x?.id === 256 && x?.other === trimmed
        );
        if (duplicateText) {
          this.otherConditionError =
            'Ya existe una condición "Otro" con ese texto.';
          return;
        }
        list.push({ id: 256, name: trimmed, other: trimmed });
        this.otherConditionCustomText = '';
      } else {
        const exists = list.some(
          (x) => x?.id === this.model.otherCondition?.id
        );

        if (exists) {
          this.$set(this.model, 'otherCondition', null);
          return;
        }

        list.push({
          id: this.model.otherCondition?.id,
          name: this.model.otherCondition?.name,
        });
      }

      this.$set(this.model, 'otherConditions', list);
      this.$set(this.model, 'otherCondition', null);
    },

    removeOtherCondition(index) {
      if (this.isReadOnly) return;

      const list = [...(this.model.otherConditions || [])];
      list.splice(index, 1);
      this.$set(this.model, 'otherConditions', list);
    },
  },
};
</script>

<style scoped lang="scss">
.participant-box {
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  background-color: #f7f7f7;
}

.participant-box__title {
  padding: 12px 16px;
  font-weight: 600;
  border-bottom: 1px solid #e9e9e9;
  background-color: #f7f7f7;
}

.primary-language-row {
  min-height: 36px;
}

.other-language-input {
  width: 240px;
  margin-top: 0 !important;
}

.other-language-input ::v-deep .form-group,
.other-language-input ::v-deep .custom-input,
.other-language-input ::v-deep .mb-3 {
  margin-bottom: 0 !important;
}

.section-divider {
  border-right: 1px solid #dcdcdc;
}

.condition-pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border: 1px solid #cfcfcf;
  border-radius: 20px;
  background: #fff;
}

.condition-pill__remove {
  margin-left: 8px;
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.primary-language-row .custom-control-inline {
  margin-right: 6rem;
}
.disabled-check ::v-deep(.bg-white) {
  background-color: #f5f5f5 !important;
  cursor: not-allowed;
  opacity: 0.7;
}

@media (max-width: 767.98px) {
  .section-divider {
    border-right: none;
    border-bottom: 1px solid #dcdcdc;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  .other-language-input {
    width: 100%;
    margin-left: 0 !important;
    margin-top: 0.75rem !important;
  }
}
::v-deep(.custom-control-input:disabled:checked
    ~ .custom-control-label::before) {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
.disabled-check {
  ::v-deep(.option-input:disabled:checked + label span) {
    background-color: #3f5b60 !important;
    border-color: #3f5b60 !important;
  }

  ::v-deep(.option-input:disabled:checked + label span .ic-check) {
    background-image: url('../../../../../../assets/img/icon-check-white.svg') !important;
    background-repeat: no-repeat !important;
    background-position: center !important;
    background-size: 12px 12px !important;

    width: 20px !important;
    height: 20px !important;
    margin: 0 !important;
    display: block !important;
  }
  ::v-deep(.bg-white) {
    background-color: #c3c3c3 !important;
    cursor: not-allowed;
    opacity: 1;
  }
}
</style>
